import Phaser from "phaser";
import { Player } from "../models/player";
import { MapHandler } from "../handlers/map-handler";
import { BaseGameScene } from "../scenes/base-game-scene";
import { LootBox } from "../models/loot-box";
import { TILE_SIZE, PLAYER_MOVE_SPEED } from "../models/common";

/**
 * Configuration for scene player movement
 */
export interface PlayerMovementConfig {
  baseSceneSpeedFactor?: number; // Default: 0.65
  showMoveAnimation?: boolean; // Default: true
}

/**
 * Handle player movement input with collision detection
 * Supports both arrow keys and WASD
 */
export function handlePlayerMovement(
  scene: BaseGameScene,
  player: Player | undefined,
  mapHandler: MapHandler | undefined,
  delta: number,
  config: PlayerMovementConfig = {},
): void {
  if (!player || !mapHandler) return;

  const { baseSceneSpeedFactor = 0.65, showMoveAnimation = true } = config;

  // Get keyboard input
  const cursors = scene.input.keyboard?.createCursorKeys();
  if (!cursors) return;

  // Calculate movement speed
  const moveSpeed =
    PLAYER_MOVE_SPEED * baseSceneSpeedFactor * 60 * (delta / 1000);

  let dx = 0;
  let dy = 0;
  let isMoving = false;

  // Check input
  if (cursors.left.isDown || scene.input.keyboard?.addKey("A").isDown) {
    dx = -1;
    isMoving = true;
  }
  if (cursors.right.isDown || scene.input.keyboard?.addKey("D").isDown) {
    dx = 1;
    isMoving = true;
  }
  if (cursors.up.isDown || scene.input.keyboard?.addKey("W").isDown) {
    dy = -1;
    isMoving = true;
  }
  if (cursors.down.isDown || scene.input.keyboard?.addKey("S").isDown) {
    dy = 1;
    isMoving = true;
  }

  if (isMoving) {
    // Calculate intended new position
    const newX = player.x + dx * moveSpeed;
    const newY = player.y + dy * moveSpeed;

    // Get map bounds
    const mapWidthPixels = mapHandler.getMapWidthPixels() || 64 * TILE_SIZE;
    const mapHeightPixels = mapHandler.getMapHeightPixels() || 64 * TILE_SIZE;
    const playerSize = 16;

    // Clamp to map bounds
    const clampedX = Math.max(
      playerSize,
      Math.min(mapWidthPixels - playerSize, newX),
    );
    const clampedY = Math.max(
      playerSize,
      Math.min(mapHeightPixels - playerSize, newY),
    );

    // Check collision with offset for vertical movement
    const checkOffsetY = dy > 0 ? 16 : 0;
    const checkX = clampedX;
    const checkY = clampedY + checkOffsetY;

    // Only move if not blocked
    if (!mapHandler.isBlocked(checkX, checkY)) {
      // Flip sprite based on direction
      if (dx < 0) {
        player.setFacing(true);
      } else if (dx > 0) {
        player.setFacing(false);
      }

      // Update player position
      player.x = clampedX;
      player.y = clampedY;
      player.targetX = clampedX;
      player.targetY = clampedY;
      player.container.setPosition(clampedX, clampedY);

      // Animation
      if (showMoveAnimation) {
        player.startMovingAnimation();
      }
    }
  } else {
    // Stop animation
    if (showMoveAnimation) {
      player.stopMovingAnimation();
    }
  }
}

/**
 * Update loot box interaction highlighting
 * Shows/hides interaction buttons based on proximity to loot boxes
 */
export interface LootBoxHighlightConfig {
  singleItemLabel?: string; // Default: "PICK UP"
  multiItemLabel?: string; // Default: "OPEN"
}

export function updateLootBoxHighlighting(
  scene: BaseGameScene,
  player: Player | undefined,
  windowManager: any,
  lootBoxes: LootBox[] | undefined,
  config: LootBoxHighlightConfig = {},
): void {
  if (!player || !windowManager || !lootBoxes) return;

  const { singleItemLabel = "PICK UP", multiItemLabel = "OPEN" } = config;
  const blockLayer = (scene as any).mapHandler?.getLayer("tile-objects-block");
  if (!blockLayer) return;

  const tileX = Math.floor(player.x / blockLayer.tilemap.tileWidth);
  const tileY = Math.floor(player.y / blockLayer.tilemap.tileHeight);

  // Update highlights
  (scene as any).interactionManager?.updateHighlights(
    scene,
    tileX,
    tileY,
    player,
    [],
    lootBoxes,
  );

  const highlightedLootBox = (scene as any).interactionManager?.getHighlightedLootBox?.();
  const found = highlightedLootBox !== null;
  const lootBoxChanged = (scene as any).interactionManager?.hasLootBoxChanged?.();

  // Track tile position changes
  const playerTileChanged =
    tileX !== (scene as any).lastPlayerTileX ||
    tileY !== (scene as any).lastPlayerTileY;

  if (playerTileChanged) {
    (scene as any).lastPlayerTileX = tileX;
    (scene as any).lastPlayerTileY = tileY;
  }

  // Generate button label and callback
  let buttonLabel: string | null = null;
  let buttonCallback: (() => void) | null = null;

  if (found && highlightedLootBox && !windowManager.isLootWindowVisible?.()) {
    const isSingleItem: boolean =
      !highlightedLootBox.info.isAirDrop &&
      highlightedLootBox.info.items.length === 1;

    buttonLabel = isSingleItem ? singleItemLabel : multiItemLabel;
    buttonCallback = () => {
      const currentLootBox =
        (scene as any).interactionManager?.getHighlightedLootBox?.();
      if (!currentLootBox) return;

      windowManager.showLootActionButtons?.(
        scene,
        currentLootBox.info,
        currentLootBox.fixed,
        () => {
          window.socket?.emit("open-loot-box", {
            x: currentLootBox.info.x,
            y: currentLootBox.info.y,
          });
        },
      );
    };
  }

  // Only update button if label changed or loot box changed
  if (buttonLabel !== (scene as any).lastButtonLabel || lootBoxChanged) {
    (scene as any).lastButtonLabel = buttonLabel;

    if (buttonLabel && buttonCallback) {
      windowManager.hideOpenButton?.();
      windowManager.hideLootActionButtons?.();
      buttonCallback();
    } else {
      if (windowManager.isOpenButtonVisible?.()) {
        windowManager.hideOpenButton?.();
      }
      windowManager.hideLootActionButtons?.();
    }
  }
}